<template>
  <div class="nx-content-selector">
    <v-layout row align-center>
      <v-autocomplete
          :disabled="disabled"
          :items="items"
          :loading="loading"
          :search-input.sync="searchInput"
          :value="boardId"
          @blur="onBlur"
          @focus="$emit('focus')"
          @input="acceptSelection"
          menu-props="closeOnContentClick"
          ref="selector"
          v-bind="$attrs"
      >
        <template v-slot:append>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                  @click="openSearchDialog"
                  v-on="on"
              >
                search
              </v-icon>
            </template>
            <span>{{ $t('message.board.search') }}</span>
          </v-tooltip>
        </template>

        <template slot="selection" slot-scope="{ item, selected }">
          <nx-chip
              :icon="item.icon"
              :selected="selected"
              :text="item.text"
              :title="$t('message.common.delete')"
              @input="remove(item)"
              close
          ></nx-chip>
        </template>

        <template slot="item" slot-scope="{ item }">
          <v-list-tile-avatar>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title
                v-html="$options.filters.highlight(item.text, searchInput)"></v-list-tile-title>
          </v-list-tile-content>
        </template>

        <template slot="no-data">
          <v-list-tile>
            <v-list-tile-avatar>
              <v-progress-circular
                  :width="2"
                  indeterminate
                  size="28"
                  v-if="loading"
              ></v-progress-circular>
              <v-icon v-html="noDataIcon" v-else></v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title v-html="noDataTitle"></v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </template>
      </v-autocomplete>

      <v-tooltip bottom v-if="showBoardCreationButton">
        <template v-slot:activator="{ on }">
          <v-menu content-class="white" transition="slide-y-transition" v-on="on">
            <v-btn
                slot="activator"
                :disabled="computedDisableBoardCreation"
                icon
                flat
                color="primary"
                class="pa-0 ma-0 ml-1"
                v-on="on"
            >
              <v-icon>add</v-icon>
            </v-btn>
            <v-list-tile
                v-for="wizard in boardWizards"
                :key="wizard.name"
                @click="openWizardDialog(wizard)">
              <v-list-tile-avatar v-if="wizard.icon">
                <v-icon v-html="wizard.icon"></v-icon>
              </v-list-tile-avatar>
              <v-list-tile-title>{{ $t('message.wizard.' + wizard.name) }}</v-list-tile-title>
            </v-list-tile>
          </v-menu>
        </template>
        <span>{{ $t('message.wizard.new.unlinked') }}</span>
      </v-tooltip>

      <template>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
                :to="editBoardUrl"
                class="pa-0 ma-0"
                color="primary"
                icon
                flat
                target="_blank"
                v-on="on">
              <v-icon>edit</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('message.board.edit') }}</span>
        </v-tooltip>
      </template>
    </v-layout>

    <nx-searcher-dialog
        v-if="searchDialogState.render"
        :active="searchDialogState.active"
        entity="board"
        :entityTabs="false"
        :filters="filters"
        :filtersParams="additionalFiltersParams"
        :disabledFilters="disabledFilters"
        :limit="1"
        :text="searchInput"
        :value="searcherValue"
        @dismiss="closeSearchDialog"
        @input="acceptSearcherValue"
        title="Buscador"
    >
    </nx-searcher-dialog>

    <nx-entity-wizard
        v-if="wizardDialogState.render"
        :active="wizardDialogState.active"
        :entity="wizardComponent.name"
        :config="wizardComponent"
        @dismiss="closeWizardDialog"
        @entityCreated="acceptEntityCreated"
    ></nx-entity-wizard>

  </div>
</template>


<script>
import axios from 'axios';
import NxSearcherDialog from '@/modules/searcher/SearcherDialog';
import NxChip from '@/components/chips/SelectorChip';
import { toArray } from '@/utils';
import {
  debounce, isNumber, isString, isObject,
} from 'lodash';
import NxEntityWizard from '@/modules/entity/wizard/Wizard';
import AllowedBoardsOnly from '@/modules/board/mixins/allowed-boards-only';

import api from '../api';

/**
 * Board selector module.
 *
 * @property {string} [identifier] String to use as identifier for this module.
 * @property {[]} [value] Array of initial selected boards.
 */
export default {
  name: 'nx-board-selector',
  extends: {},
  mixins: [AllowedBoardsOnly],
  components: {
    NxChip,
    NxSearcherDialog,
    NxEntityWizard,
  },
  props: {
    additionalFilters: {
      type: Object,
      default: () => ({}),
    },
    additionalFiltersParams: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
    },
    allowBoardCreation: {
      type: Boolean,
      default: true,
    },
    disableBoardCreation: {
      type: Boolean,
    },
    value: {
      type: [Object, String, Number],
    },
    disabledFilters: {
      type: Array,
      default: () => ([]),
    },
    wizards: {
      type: Array,
      default: () => ([]),
    },
  },
  data: () => ({
    cards: [],
    filters: {},
    internalValue: null,
    loadingCount: 0,
    searchDialogState: {
      render: false,
      active: false
    },
    searchInput: '',
    searchTokenSource: {},
    wizardComponent: null,
    wizardDialogState: {
      render: false,
      active: false
    },
  }),
  computed: {
    searcherValue() {
      return toArray(this.internalValue);
    },
    items() {
      let items = this.internalValue ? [this.internalValue] : [];
      if (!this.loading) items = items.concat(this.cards);
      return items.map(this.cardToItem);
    },
    loading: {
      set(val) {
        if (val) {
          this.loadingCount += 1;
        } else {
          this.loadingCount = Math.max((this.loadingCount - 1), 0);
        }
      },
      get() {
        return Boolean(this.loadingCount);
      },
    },
    /**
     * Icon used in the no-data slot.
     */
    noDataIcon() {
      if (this.searchInput) return 'not_interested';
      return 'edit';
    },
    /**
     * Title used in the no-data slot.
     */
    noDataTitle() {
      if (this.loading) return this.$t('message.common.loading');
      if (this.searchInput) return this.$t('message.search.noDataFound');
      return this.$t('message.search.typeToSearch');
    },
    boardId() {
      if (!this.internalValue
          || !this.internalValue.id) {
        return null;
      }
      return this.internalValue.id;
    },
    customBoardWizards() {
      return this.wizards
          .map(wizard => this.$store.getters.getWizard(wizard))
          .filter(wizard => wizard);
    },
    boardWizards() {
      if (this.customBoardWizards.length > 0) {
        return this.customBoardWizards;
      }
      const searcherConfig = this.$store.getters.searcher;
      if (!searcherConfig.board
          && !Array.isArray(searcherConfig.board.wizards)
          && searcherConfig.board.wizards.length === 0) {
        return [];
      }
      return searcherConfig.board.wizards.map(wizard => this.$store.getters.getWizard(wizard));
    },
    showBoardCreationButton() {
      return this.allowBoardCreation && this.wizards.length > 0 && this.boardWizards.length > 0;
    },
    computedDisableBoardCreation() {
      return this.disableBoardCreation || this.disabled;
    },
    editBoardUrl() {
      return this.boardId
          ? {
            name: 'board.editor',
            params: { id: this.boardId }
          }
          : null;
    },

  },
  methods: {
    acceptSelection(boardId) {
      this.internalValue = this.cards.find(board => board.id === boardId);
      this.$emit('input', this.boardId, this.internalValue);
    },
    acceptSearcherValue(value) {
      value = value.length > 0 ? value[0] : null;
      this.internalValue = this.filterAllowedBoards(value, this.$t('message.composition.error.nonAllowedBoardSelectedWarning'));
      this.$emit('input', this.boardId, this.internalValue);
    },
    acceptEntityCreated(board) {
      this.internalValue = board;
      this.$emit('input', this.boardId, this.internalValue);
    },
    /**
     * Cancels the current search operation.
     * @param {string} reason
     */
    cancelCurrentSearch(reason = '') {
      if (this.searchTokenSource.cancel) this.searchTokenSource.cancel(reason);
      this.searchTokenSource = {};
    },
    /**
     * Transforms a UrlCard to an item, to be displayed as option in the VSelect.
     * @param {{}} boardCard
     * @return {{value: *, icon: *, text: string}}
     */
    cardToItem(boardCard) {
      return {
        icon: 'dashboard',
        text: boardCard.title,
        value: boardCard.id,
      };
    },
    /**
     * Closes the searcher dialog.
     */
    closeSearchDialog() {
      this.searchDialogState.active = false;
      this.$nextTick(() => {
        this.searchDialogState.render = false;
      });
    },
    async fetchBoard(boardId) {
      try {
        return await api.fetch(boardId);
      } catch (e) {
        return false;
      }
    },
    /**
     * Closes the wizard dialog.
     */
    closeWizardDialog() {
      this.wizardDialogState.active = false;
      this.$nextTick(() => {
        this.wizardDialogState.render = false;
      });
    },
    /**
     * Finds a board by its given identifier in the component's cards.
     */
    findCard(boardId) {
      if (this.boardId === boardId) return this.internalValue;
      return this.cards.find(card => card.id === boardId);
    },
    onBlur() {
      this.$emit('blur', this.boardId);
    },
    /**
     * Deletes the selected board.
     */
    async remove() {
      this.internalValue = null;
      this.$emit('blur', this.boardId);
      this.$emit('input', this.boardId, this.internalValue);
    },
    /**
     * Opens the searcher dialog.
     */
    openSearchDialog() {
      this.searchDialogState.render = true;
      this.$nextTick(() => {
        this.searchDialogState.active = true;
      });
    },
    /**
     * Opens the board wizard with the given configuration.
     */
    openWizardDialog(wizard) {
      if (this.computedDisableBoardCreation) return;
      this.wizardComponent = wizard;
      this.wizardDialogState.render = true;
      this.$nextTick(() => {
        this.wizardDialogState.active = true;
      });
    },
    /**
     * Performs a textual search. If a search is being performed currently, cancels it before.
     */
    // eslint-disable-next-line
    performSearch: debounce(async function () {
      this.cancelCurrentSearch();
      this.searchTokenSource = axios.CancelToken.source();

      this.loading = true;
      const response = await api.search(this.filters, this.searchTokenSource);
      this.loading = false;

      this.cards = response.results;
    }, 300),
  },
  watch: {
    /**
     * Whenever the search input changes, modify the search filters and perform a new search.
     */
    searchInput(val) {
      this.searchInput = val;
      if (val !== '' && val !== null) {
        this.filters.text = val;
      } else {
        delete this.filters.text;
      }
      this.performSearch();
    },
    /**
     * Whenever the value changes, recompute the internal value.
     * The functions searches for the cards of the given board inside its internal state. If
     * they are not found, it fetches the information from the server.
     */
    value: {
      async handler(newValue) {
        let value = newValue;
        if (value === null) {
          this.internalValue = null;
          return;
        }
        this.loading = true;
        if (isNumber(value) || isString(value)) {
          const item = this.findCard(value);
          if (item) {
            value = item;
          } else {
            value = await this.fetchBoard(value);
          }
        }
        if (isObject(value)) this.internalValue = value;
        this.loading = false;
      },
      immediate: true,
    },
  },
  mounted() {
    this.filters = { ...this.filters, ...this.additionalFilters };
    this.performSearch();
  },
};
</script>
